<script setup lang="ts">
import {computed, nextTick, type PropType, ref, toRaw} from 'vue';
import {ORGANIZATION_CHANGE_EMIT, ORGANIZATION_REMOVE_EMIT} from '@/base/cabConstants';
import {
    CabObj,
    type CabOrganization,
    CabTheme,
    enum_values,
    uuidOf,
    WachttijdApiTypeEnum,
    WachttijdApiTypeEnum_name,
} from 'common';
import DeleteButton from '@/views/icons/DeleteIcon.vue';
import {ElButton, ElCascader, ElDialog, ElInputNumber, ElOption, ElSelect} from 'element-plus';

const emits = defineEmits([ORGANIZATION_REMOVE_EMIT, ORGANIZATION_CHANGE_EMIT]);
const props = defineProps({
    organization: {
        type    : Object as PropType<CabOrganization>,
        required: true,
    },
    allThemes: {
        type    : Object as PropType<CabTheme[]>,
        required: true,
    },
});
defineExpose({
    firstEdit,
});

const organization     = ref<CabOrganization>(props.organization);
const allThemesRef     = computed(() => makeCascaderOptions(props.allThemes));
const selectedThemes   = ref(props.organization.themes.map((t) => [uuidOf(t)]));
const nameField        = ref<HTMLInputElement | null>(null);
const nameEditing      = ref<boolean>(false);
const kvkField         = ref<HTMLInputElement | null>(null);
const kvkEditing       = ref<boolean>(false);
const apiField         = ref<HTMLInputElement | null>(null);
const apiDialogVisible = ref<boolean>(false);

let isFirstEdit: boolean = false;

function makeCascaderOptions<T extends CabObj>(list: T[]): any {
    return list.map((o) => ({
        label: (o as any).name, 
        value: uuidOf(o),
    }));
}

function firstEdit() {
    isFirstEdit = true;
    startEditingName();
}

//==========================================
function startEditingName() {
    nameEditing.value = true;
    nextTick(() => nameField.value!.focus());
}

function stopEditingName() {
    nameEditing.value = false;
    nameField.value!.blur();
    if (isFirstEdit) {
        if (!props.organization.name.trim()) {
            onDelete();
        }
        isFirstEdit = false;
    }
}

//==========================================
function startEditingKvk() {
    kvkEditing.value = true;
    nextTick(() => nextTick(() => kvkField.value!.focus()));
}

function stopEditingKvk() {
    kvkEditing.value = false;
    kvkField.value!.blur();
}

//==========================================
function dynInputClass(b: boolean) {
    return b ? `bg-white border-gray-300 rounded-md shadow-xl` : `bg-yellow-100`;
}

function clearApi() {
    organization.value.wachttijdApiType = undefined;
    apiField.value?.blur();
}

function onDelete() {
    emits(ORGANIZATION_REMOVE_EMIT, organization.value);
}

function onChangeThemes() {
    const rawSelected         = toRaw(selectedThemes.value).map((l: any) => toRaw(l)[0]);
    organization.value.themes = props.allThemes?.filter((theme) =>
        rawSelected.includes(uuidOf(theme)),
    );
    onChange();
}

function onChange() {
    emits(ORGANIZATION_CHANGE_EMIT, organization.value);
}

</script>

<template>
    <div
        class="flex flex-row items-center w-full font-medium m-0.5 p-1 bg-yellow-100 text-gray-900"
        data-testid="thema-strip"
    >
        <div class="w-96 px-3 py-1" title="instelling naam">
            <input
                class="mt-1 mr-1 px-2 py-1 border-1 w-full"
                :class="dynInputClass(nameEditing)"
                type="text"
                ref="nameField"
                v-model="organization.name"
                @input="onChange"
                @click="startEditingName"
                @focus="startEditingName"
                @keyup.enter="stopEditingName()"
                @blur="stopEditingName()"
                placeholder="name"
                data-testid="thema-name"
            />
        </div>

        <div class="p-1 flex-1" title="thema's voor deze instelling" data-testid="thema-select">
            <el-cascader
                class="w-full"
                v-model="selectedThemes"
                :options="allThemesRef"
                :props="{ multiple: true }"
                clearable
                placeholder="selecteer thema's"
                @change="onChangeThemes"
            />
        </div>

        <el-input-number
            class="w-28 mx-1"
            v-model="organization.deeltijdfactor"
            :controls="false"
            :precision="3"
            :min="0"
            :max="1"
            placeholder="deeltijd"
            title="deeltijdfactor: fte per medewerker, voor het dashboard"
            @change="onChange"
        />

        <el-button type="primary"
                   @click="apiDialogVisible = true"
                   class="w-24 mx-3"
                   :class="organization.kvkNumber||organization.wachttijdApiType ? 'font-extrabold' : 'font-thin'"
                   data-testid="api-button"
        >
            API
        </el-button>

        <DeleteButton @click="onDelete()"/>
    </div>

    <el-dialog
        v-model="apiDialogVisible"
        :close-on-press-escape="true"
        :close-on-click-modal="true"
        :title="`Wachttijden API gerelateerde informatie voor ${organization.name}`"
    >
        <div class="flex flex-col min-w-60 min-h-40 bg-gray-300 p-3">

            <div class="flex flex-row items-center w-full px-3 py-3">
                <div class="text-gray-500 mx-4 w-32 text-right">
                    kvk nummer:
                </div>
                <input
                    class="mt-1 mr-1 px-2 py-1 border-1"
                    :class="dynInputClass(kvkEditing)"
                    type="text"
                    ref="kvkField"
                    v-model="organization.kvkNumber"
                    @input="onChange"
                    @click="startEditingKvk()"
                    @focus="startEditingKvk()"
                    @keyup.enter="stopEditingKvk()"
                    @blur="stopEditingKvk()"
                    placeholder="kvk nummer"
                />
            </div>

            <div class="flex flex-row items-center w-full px-3 py-3">
                <div class="text-gray-500 mx-4 w-32 text-right">
                    API type:
                </div>
                <el-select
                    v-model="organization.wachttijdApiType"
                    placeholder="API"
                    @change="onChange"
                    class="w-44"
                    ref="apiField"
                    clearable
                    @keyup.delete="clearApi()"
                >
                    <el-option
                        v-for="item in enum_values(WachttijdApiTypeEnum)"
                        :key="item"
                        :label="WachttijdApiTypeEnum_name(item)"
                        :value="item"
                    />
                </el-select>
            </div>
        </div>
    </el-dialog>

</template>
